import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material/styles';
import {
  PortalTableCell,
  PortalTableHead,
  PortalTableRow,
} from '../../../../styles/portalListTable';
import { formatPhoneDisplay } from '../../../../utils/formatPhoneDisplay';
import { GuestListAddAttendeeAction } from '../../../Events/widgets/AddAttendeeModal';
import DeleteAttendeeDialog from './DeleteAttendeeDialog';
import {
  filterPartnerGuests,
  formatGuestPaidAt,
  getGuestAttendeeId,
  GUEST_LIST_ROWS_PER_PAGE,
  PARTNER_GUEST_PAYMENT_FILTERS,
} from './partnerEventGuestListUtils';

const roundedInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: 'background.paper',
    fontSize: '0.875rem',
  },
};

const FilterPill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px 18px',
  borderRadius: 9999,
  fontSize: '0.8125rem',
  fontWeight: 600,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'background-color 0.15s ease, color 0.15s ease',
  backgroundColor: active ? theme.palette.primary.main : theme.palette.action.hover,
  color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : theme.palette.action.selected,
  },
}));

const PAYMENT_BADGE_STYLES = {
  Paid: { bg: '#E8F5E9', color: '#2E7D32' },
  'Out of Platform': { bg: '#EDE7F6', color: '#5E35B1' },
  Pending: { bg: '#FFF3E0', color: '#E65100' },
  Refunded: { bg: '#FCE4EC', color: '#C62828' },
  Bypassed: { bg: '#E3F2FD', color: '#1565C0' },
};

const RSVP_BADGE_STYLES = {
  Confirmed: { bg: '#E8F5E9', color: '#2E7D32' },
  Waitlist: { bg: '#F3E5F5', color: '#6A1B9A' },
};

const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'paletteKey' && prop !== 'variant',
})(({ paletteKey, variant = 'payment' }) => {
  const map = variant === 'rsvp' ? RSVP_BADGE_STYLES : PAYMENT_BADGE_STYLES;
  const { bg, color } = map[paletteKey] ?? { bg: '#f5f5f5', color: '#616161' };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 14px',
    borderRadius: 9999,
    backgroundColor: bg,
    color,
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  };
});

const RefundButton = styled(LoadingButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  borderColor: theme.palette.divider,
  borderRadius: 8,
  padding: '6px 14px',
  '&:hover': {
    borderColor: theme.palette.text.disabled,
    backgroundColor: theme.palette.background.default,
  },
}));

const DeleteButton = styled(LoadingButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.error.main,
  borderColor: theme.palette.error.light,
  borderRadius: 8,
  padding: '6px 14px',
  '&:hover': {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.light,
  },
}));

const PartnerEventGuestList = ({
  guests = [],
  eventId,
  onRefund,
  onDeleteAttendee,
  refundingBookingId = null,
  deletingAttendeeId = null,
}) => {
  const showRefundActions = Boolean(onRefund);
  const showDeleteActions = Boolean(onDeleteAttendee);
  const hasOutOfPlatformGuests = useMemo(
    () => guests.some((guest) => guest.outOfPlatform),
    [guests],
  );
  const showActions = showRefundActions || (showDeleteActions && hasOutOfPlatformGuests);
  const isAnyRefundInProgress = Boolean(refundingBookingId);
  const isAnyDeleteInProgress = Boolean(deletingAttendeeId);
  const tableColumnCount = showActions ? 6 : 5;

  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [attendeeToDelete, setAttendeeToDelete] = useState(null);

  const filteredGuests = useMemo(
    () => filterPartnerGuests(guests, { search, paymentFilter }),
    [guests, search, paymentFilter],
  );

  const paginatedGuests = useMemo(() => {
    const start = page * GUEST_LIST_ROWS_PER_PAGE;
    return filteredGuests.slice(start, start + GUEST_LIST_ROWS_PER_PAGE);
  }, [filteredGuests, page]);

  useEffect(() => {
    setPage(0);
  }, [search, paymentFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredGuests.length / GUEST_LIST_ROWS_PER_PAGE) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredGuests.length, page]);

  const handleConfirmDelete = async () => {
    const attendeeId = getGuestAttendeeId(attendeeToDelete);
    if (!attendeeId || !onDeleteAttendee || isAnyDeleteInProgress) return;

    try {
      await onDeleteAttendee(attendeeId);
      setAttendeeToDelete(null);
    } catch {
      // Parent handles error toast.
    }
  };

  if (!guests.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <GuestListAddAttendeeAction eventId={eventId} />
        <GuestListToolbar
          search={search}
          onSearchChange={(e) => setSearch(e.target.value)}
          paymentFilter={paymentFilter}
          onPaymentFilterChange={setPaymentFilter}
        />
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No guests yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <GuestListAddAttendeeAction eventId={eventId} />
      <GuestListToolbar
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        paymentFilter={paymentFilter}
        onPaymentFilterChange={setPaymentFilter}
      />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <Table>
          <PortalTableHead>
            <TableRow>
              <TableCell>Attendee</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Paid At</TableCell>
              <TableCell>RSVP</TableCell>
              {showActions ? <TableCell align="right">Actions</TableCell> : null}
            </TableRow>
          </PortalTableHead>
          <TableBody>
            {filteredGuests.length === 0 ? (
              <PortalTableRow>
                <PortalTableCell colSpan={tableColumnCount}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2, textAlign: 'center' }}
                  >
                    No guests match your search or filters
                  </Typography>
                </PortalTableCell>
              </PortalTableRow>
            ) : (
              paginatedGuests.map((guest) => {
                const bookingKey = guest.bookingId ?? guest.id;
                const attendeeId = getGuestAttendeeId(guest);
                const isRowRefunding = refundingBookingId === bookingKey;
                const isRowDeleting = deletingAttendeeId === attendeeId;
                const disableRefund =
                  guest.paymentStatus !== 'Paid' || (isAnyRefundInProgress && !isRowRefunding);
                const disableDelete = isAnyDeleteInProgress && !isRowDeleting;

                return (
                  <PortalTableRow key={guest.id}>
                    <PortalTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={guest.avatarUrl || undefined}
                          alt={guest.name}
                          sx={{ width: 40, height: 40 }}
                        >
                          {!guest.avatarUrl && guest.name?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {guest.name}
                        </Typography>
                      </Box>
                    </PortalTableCell>
                    <PortalTableCell>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {guest.email !== '—' ? guest.email : '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {formatPhoneDisplay(guest.phone)}
                      </Typography>
                    </PortalTableCell>
                    <PortalTableCell>
                      <StatusBadge paletteKey={guest.paymentStatus} variant="payment">
                        {guest.paymentStatus}
                      </StatusBadge>
                    </PortalTableCell>
                    <PortalTableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {formatGuestPaidAt(guest.paidAt)}
                      </Typography>
                    </PortalTableCell>
                    <PortalTableCell>
                      <StatusBadge paletteKey={guest.rsvpStatus} variant="rsvp">
                        {guest.rsvpStatus}
                      </StatusBadge>
                    </PortalTableCell>
                    {showActions ? (
                      <PortalTableCell align="right">
                        {guest.outOfPlatform && showDeleteActions ? (
                          <DeleteButton
                            variant="outlined"
                            size="small"
                            loading={isRowDeleting}
                            disabled={disableDelete}
                            loadingPosition="start"
                            startIcon={
                              !isRowDeleting ? (
                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                              ) : undefined
                            }
                            onClick={() => setAttendeeToDelete(guest)}
                          >
                            {isRowDeleting ? 'Deleting…' : 'Delete'}
                          </DeleteButton>
                        ) : showRefundActions ? (
                          <RefundButton
                            variant="outlined"
                            size="small"
                            loading={isRowRefunding}
                            disabled={disableRefund}
                            loadingPosition="start"
                            startIcon={
                              !isRowRefunding ? <ReplayIcon sx={{ fontSize: 18 }} /> : undefined
                            }
                            onClick={() => onRefund(bookingKey)}
                          >
                            {isRowRefunding ? 'Refunding…' : 'Refund'}
                          </RefundButton>
                        ) : null}
                      </PortalTableCell>
                    ) : null}
                  </PortalTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredGuests.length > 0 ? (
        <TablePagination
          component="div"
          count={filteredGuests.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={GUEST_LIST_ROWS_PER_PAGE}
          rowsPerPageOptions={[GUEST_LIST_ROWS_PER_PAGE]}
          onRowsPerPageChange={() => {}}
        />
      ) : null}

      <DeleteAttendeeDialog
        open={Boolean(attendeeToDelete)}
        attendee={attendeeToDelete}
        deleting={isAnyDeleteInProgress}
        onClose={() => {
          if (!isAnyDeleteInProgress) setAttendeeToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

const GuestListToolbar = ({ search, onSearchChange, paymentFilter, onPaymentFilterChange }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <TextField
      size="small"
      placeholder="Search"
      value={search}
      onChange={onSearchChange}
      sx={{ flex: '1 1 200px', minWidth: 180, maxWidth: 320, ...roundedInputSx }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          </InputAdornment>
        ),
      }}
    />
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {PARTNER_GUEST_PAYMENT_FILTERS.map((filter) => (
        <FilterPill
          key={filter.id}
          active={paymentFilter === filter.id}
          onClick={() => onPaymentFilterChange(filter.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onPaymentFilterChange(filter.id);
            }
          }}
        >
          {filter.label}
        </FilterPill>
      ))}
    </Box>
  </Box>
);

export default PartnerEventGuestList;
